import {useRouter} from 'next/router';
import {useIntl} from 'react-intl';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Heading,
  Avatar,
  Flex,
  Text,
  Button,
  Center,
} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {TShirtIcon} from '@eksab/assets/icons';
import type {Player} from '@eksab/features/competition/types';
import {useSetQueryPlayerUIState} from '@eksab/features/competition/lineup';
import {useLineupStore} from '@eksab/features/competition/lineup/useLineupStore';

import {StatsTable, ChangeRoleButton, RoleIndicator, WarningIndicator, NoStats} from './';
import {CaptainIcon, ViceCaptainIcon} from './icons';
import {useAddPlayer} from '@eksab/features/competition/lineup/useAddPlayer';

type PlayerStatsModalProps = {
  hasActions: boolean;
};

type Stat = keyof Player['stats'];

export const PlayerStatsModal = ({hasActions}: PlayerStatsModalProps) => {
  const competitionId = useRouter().query.slug as string;
  const setQueryPlayerUIState = useSetQueryPlayerUIState(competitionId);
  const format = useIntl().formatNumber;

  const t = useTranslate();
  const openDrawer = useLineupStore((state) => state.openDrawer);
  const playerStatsModal = useLineupStore((state) => state.playerStatsModal);
  const setPlayerStatsModal = useLineupStore((state) => state.setPlayerStatsModal);
  const selectCaptain = useLineupStore((state) => state.selectCaptain);
  const selectViceCaptain = useLineupStore((state) => state.selectViceCaptain);
  const removePlayer = useLineupStore((state) => state.removePlayer);

  const addPlayer = useAddPlayer(competitionId);
  const onClose = () => setPlayerStatsModal(null);

  const onActionClick = (action?: Function) => () => {
    action?.();
    onClose();
  };

  const onAddPlayer = () => {
    addPlayer(playerStatsModal!.player);
    mixpanel?.track('Player Added');
    setQueryPlayerUIState(
      `${playerStatsModal!.player.team.id}`,
      playerStatsModal!.player.position,
      playerStatsModal!.player.id,
      'add',
    );
  };

  const onRemovePlayer = () => {
    removePlayer(playerStatsModal!.player, playerStatsModal!.isSubstitute);
    setQueryPlayerUIState(
      `${playerStatsModal!.player.team.id}`,
      playerStatsModal!.player.position,
      playerStatsModal!.player.id,
      'remove',
    );
  };

  const onChangePlayer = () => {
    onRemovePlayer();
    openDrawer();
  };

  const onSelectCaptain = () => selectCaptain(playerStatsModal!.player, playerStatsModal!.isSubstitute);

  const onSelectViceCaptain = () => selectViceCaptain(playerStatsModal!.player, playerStatsModal!.isSubstitute);

  const isLineupPlayer = playerStatsModal?.context === 'lineup-player';
  const hasLeadershipRole =
    isLineupPlayer && Boolean(playerStatsModal.player.is_captain || playerStatsModal.player.is_vice_captain);
  const isInvalidPlayer =
    isLineupPlayer &&
    !!playerStatsModal.player.selection_status &&
    playerStatsModal.player.selection_status.status !== 'valid';
  const {common_name, image, position, team, price, fantasy_points} = playerStatsModal?.player ?? {};

  // Todo: Remove this after backend returns this resource in the desired format
  const stats = Object.entries(playerStatsModal?.player.stats ?? {})
    .filter(([_, {value}]) => value !== null)
    .map(([stat, {value}]) => ({name: stat as Stat, value: value as number}));

  return playerStatsModal ? (
    <Modal isOpen={!!playerStatsModal} onClose={onClose} isCentered motionPreset="slideInBottom" size="sm">
      <ModalOverlay />

      <ModalContent
        borderWidth="3px"
        borderColor="gray.900"
        bg="gray.900"
        rounded="xl"
        shadow="sm"
        maxH="75vh"
        overflow="auto"
      >
        <ModalHeader display="flex" gap="3" px="5" py="4">
          <Avatar
            className="player-avatar"
            bg="transparent"
            rounded="initial"
            boxSize="3.75rem"
            sx={{'> img': {bg: 'white'}}}
            src={image?.thumb ?? image?.original}
            icon={<TShirtIcon filled={false} width="full" height="auto" />}
          />

          <Flex
            className="player-basic-info"
            w="full"
            minH="4.5rem"
            flexDir="column"
            color="white"
            fontWeight="700"
            overflow="hidden"
          >
            <Heading
              dir="auto"
              title={common_name}
              mb="2"
              size="xs"
              fontWeight="inherit"
              alignSelf="flex-start"
              color="inherit"
              maxW="85%"
              noOfLines={1}
            >
              {common_name}
            </Heading>

            <Text color="gray.500" fontWeight="inherit" fontSize="xs" maxW="90%" noOfLines={1}>
              {t(`fantasy.lineup.${position!}`)} . {team!.name}
            </Text>

            <Flex flex={1} gap="4" align="flex-end" justify="space-between">
              <Flex gap="2" fontSize="sm" color="gray.500">
                <Text color="white" fontWeight="inherit">
                  {t('fantasy.lineup.price-unit', {amount: format(price!)})}
                </Text>
                |
                <Text color="green.400" fontWeight="800">
                  {format(fantasy_points!)} {t('app.points')}
                </Text>
              </Flex>

              {isInvalidPlayer ? (
                <WarningIndicator />
              ) : hasLeadershipRole ? (
                <RoleIndicator role={playerStatsModal.player.is_captain ? 'captain' : 'vice-captain'} />
              ) : null}
            </Flex>
          </Flex>
        </ModalHeader>
        <ModalCloseButton color="white" size="lg" top="1" insetEnd="1" />

        <ModalBody px="min(3rem, 11vw)" py="5" bg="white">
          {stats.length ? <StatsTable stats={stats} /> : <NoStats />}
        </ModalBody>

        {hasActions && (
          <ModalFooter bg="white" flexDir="column" gap="4" px="min(3rem, 11vw)" pt="0" pb="5">
            {isLineupPlayer && (
              <Center w="full" gap="2">
                <ChangeRoleButton
                  title={t('fantasy.lineup.player.actions.captain')}
                  icon={CaptainIcon}
                  onClick={onActionClick(onSelectCaptain)}
                  isActive={!!playerStatsModal.player.is_captain}
                  isDisabled={isInvalidPlayer}
                  flexGrow={1}
                />

                <ChangeRoleButton
                  title={t('fantasy.lineup.player.actions.vice-captain')}
                  icon={ViceCaptainIcon}
                  onClick={onActionClick(onSelectViceCaptain)}
                  isActive={!!playerStatsModal.player.is_vice_captain}
                  isDisabled={isInvalidPlayer}
                  flexGrow={2}
                />
              </Center>
            )}

            {isLineupPlayer && (
              <Button w="full" tabIndex={1} onClick={onActionClick(onChangePlayer)}>
                {t('fantasy.lineup.player.actions.change')}
              </Button>
            )}

            <Button
              variant={playerStatsModal.action === 'add' ? 'outline' : 'warn'}
              w="full"
              onClick={onActionClick(playerStatsModal.action === 'add' ? onAddPlayer : onRemovePlayer)}
            >
              {t(`fantasy.lineup.player.actions.${playerStatsModal.action}`)}
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  ) : null;
};
